import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { siteDiaryAPI } from '../utils/api';
import { toast } from 'sonner';
import LineItemGrid from '../components/site-diary/LineItemGrid';
import SignaturePad from '../components/site-diary/SignaturePad';
import { STATUS_META } from './SiteDiary';
import {
  ArrowLeft, Loader2, Users, Truck, Package, TrendingUp, ShieldCheck, UserCheck,
  Camera, PenTool, History, Send, Lock, RotateCcw, Ban, FileDown, Save, Sun, Moon,
  AlertTriangle, CheckCircle2, MapPin, Upload, Trash2, ChevronRight, Cloud, Columns, Share2, Copy,
} from 'lucide-react';

const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined));

const TABS = [
  ['overview', 'Overview', Cloud],
  ['labour', 'Labour', Users],
  ['equipment', 'Equipment', Truck],
  ['materials', 'Materials', Package],
  ['progress', 'Progress', TrendingUp],
  ['safety', 'Safety', ShieldCheck],
  ['visitors', 'Visitors', UserCheck],
  ['media', 'Media', Camera],
  ['signoff', 'Sign-off', PenTool],
  ['audit', 'Audit', History],
];

export default function SiteDiaryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || '';
  const isAdmin = role === 'admin';
  const isReviewer = ['admin', 'manager', 'project_manager', 'site_manager'].includes(role);

  const [diary, setDiary] = useState(null);
  const [enums, setEnums] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [savingHeader, setSavingHeader] = useState(false);
  const [header, setHeader] = useState({});

  const load = useCallback(async () => {
    try {
      const [d, e] = await Promise.all([siteDiaryAPI.getOne(id), siteDiaryAPI.getEnums()]);
      setDiary(d.data);
      setHeader(d.data);
      setEnums(e.data);
    } catch (err) {
      toast.error('Failed to load diary');
      navigate('/site-diary');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !diary || !enums) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  const status = diary.status;
  const locked = status === 'signed_off' || status === 'void';
  const canEdit = status === 'draft' || (status === 'submitted' && isReviewer);
  const meta = STATUS_META[status] || STATUS_META.draft;

  // ---- child CRUD helpers ----
  const addRow = (entity) => async (data) => { await siteDiaryAPI.addRow(id, entity, clean(data)); await load(); };
  const updateRow = (entity) => async (rowId, data) => { await siteDiaryAPI.updateRow(id, entity, rowId, clean(data)); await load(); };
  const deleteRow = (entity) => async (rowId) => {
    try { await siteDiaryAPI.deleteRow(id, entity, rowId); await load(); toast.success('Removed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  const saveHeader = async () => {
    setSavingHeader(true);
    try {
      const payload = {
        general_conditions: header.general_conditions,
        weather_am_temp_c: header.weather_am_temp_c, weather_am_conditions: header.weather_am_conditions,
        weather_am_wind_kmh: header.weather_am_wind_kmh, weather_pm_temp_c: header.weather_pm_temp_c,
        weather_pm_conditions: header.weather_pm_conditions, weather_pm_wind_kmh: header.weather_pm_wind_kmh,
        has_delays: !!header.has_delays, delay_type: header.delay_type,
        delay_impact_hours: header.delay_impact_hours, delay_narrative: header.delay_narrative,
      };
      await siteDiaryAPI.updateHeader(id, clean(payload));
      await load();
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSavingHeader(false); }
  };

  // ---- workflow ----
  const doSubmit = async () => {
    try { await siteDiaryAPI.submit(id); await load(); toast.success('Submitted for review'); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Cannot submit — check mandatory sections (Labour, Progress, Safety)'); }
  };
  const doReject = async () => {
    const c = window.prompt('Reason for sending back to draft?');
    if (!c) return;
    try { await siteDiaryAPI.reject(id, c); await load(); toast.success('Sent back to draft'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not reject'); }
  };
  const doReopen = async () => {
    if (!window.confirm('Reopen this locked diary? A new amended version (v+1) will be created.')) return;
    try { const r = await siteDiaryAPI.reopen(id); toast.success(`Amendment v${r.data.version} created`); navigate(`/site-diary/${r.data.new_diary_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not reopen'); }
  };
  const doVoid = async () => {
    const r = window.prompt('Reason for voiding this diary?');
    if (!r) return;
    try { await siteDiaryAPI.voidDiary(id, r); await load(); toast.success('Diary voided'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not void'); }
  };
  const downloadPdf = async () => {
    try {
      const res = await siteDiaryAPI.getPdfBlob(id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) { toast.error('Could not open PDF'); }
  };

  const wc = enums.weather_conditions;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto pb-24">
      {/* Header summary */}
      <button onClick={() => navigate('/site-diary')} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3">
        <ArrowLeft className="w-4 h-4" /> All diaries
      </button>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-slate-800">{diary.project_name || 'Project'}</h1>
              {diary.project_code && <span className="text-sm text-slate-400">{diary.project_code}</span>}
              <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${meta.cls}`}>{meta.label}</span>
              {diary.version > 1 && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">v{diary.version}</span>}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {diary.diary_date} · Created by {diary.created_by_name || '—'}
              {diary.signed_off_by_name && ` · Approved by ${diary.signed_off_by_name}`}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 mb-4 -mx-1 px-1">
        {TABS.map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`dtab-${k}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === k ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <Icon className="w-4 h-4" />{lbl}
          </button>
        ))}
      </div>

      {locked && (
        <div className="mb-4 flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 text-slate-600 rounded-lg px-3 py-2">
          <Lock className="w-4 h-4" /> This diary is read-only{status === 'signed_off' ? ' (Approved & Locked).' : ' (Voided).'}
        </div>
      )}

      {/* ---- OVERVIEW ---- */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Cloud className="w-4 h-4 text-teal-600" /> Weather & Site Conditions</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[['am', 'Morning', Sun], ['pm', 'Afternoon', Moon]].map(([p, lbl, Icon]) => (
                <div key={p} className="border border-slate-100 rounded-lg p-3">
                  <div className="text-sm font-medium text-slate-600 flex items-center gap-1 mb-2"><Icon className="w-4 h-4" />{lbl}</div>
                  <div className="grid grid-cols-3 gap-2">
                    <input disabled={!canEdit} type="number" placeholder="°C" value={header[`weather_${p}_temp_c`] ?? ''}
                      onChange={(e) => setHeader((h) => ({ ...h, [`weather_${p}_temp_c`]: e.target.value === '' ? '' : Number(e.target.value) }))}
                      className="px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-50" />
                    <select disabled={!canEdit} value={header[`weather_${p}_conditions`] ?? ''}
                      onChange={(e) => setHeader((h) => ({ ...h, [`weather_${p}_conditions`]: e.target.value }))}
                      className="px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-50">
                      <option value="">Conditions</option>
                      {wc.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input disabled={!canEdit} type="number" placeholder="km/h" value={header[`weather_${p}_wind_kmh`] ?? ''}
                      onChange={(e) => setHeader((h) => ({ ...h, [`weather_${p}_wind_kmh`]: e.target.value === '' ? '' : Number(e.target.value) }))}
                      className="px-2 py-1.5 text-sm border border-slate-300 rounded-md disabled:bg-slate-50" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-slate-600">General site conditions / narrative</label>
              <textarea disabled={!canEdit} rows={3} value={header.general_conditions ?? ''}
                onChange={(e) => setHeader((h) => ({ ...h, general_conditions: e.target.value }))}
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <label className="flex items-center gap-2 font-semibold text-slate-800">
              <input type="checkbox" disabled={!canEdit} checked={!!header.has_delays}
                onChange={(e) => setHeader((h) => ({ ...h, has_delays: e.target.checked }))} className="w-4 h-4 accent-teal-600" />
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Delays occurred today
            </label>
            {header.has_delays && (
              <div className="grid md:grid-cols-3 gap-3 mt-3">
                <select disabled={!canEdit} value={header.delay_type ?? ''} onChange={(e) => setHeader((h) => ({ ...h, delay_type: e.target.value }))}
                  className="px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50">
                  <option value="">Delay type</option>
                  {enums.delay_types.map((d) => <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>)}
                </select>
                <input disabled={!canEdit} type="number" placeholder="Impact (hours)" value={header.delay_impact_hours ?? ''}
                  onChange={(e) => setHeader((h) => ({ ...h, delay_impact_hours: e.target.value === '' ? '' : Number(e.target.value) }))}
                  className="px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50" />
                <input disabled={!canEdit} placeholder="Narrative" value={header.delay_narrative ?? ''}
                  onChange={(e) => setHeader((h) => ({ ...h, delay_narrative: e.target.value }))}
                  className="px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50 md:col-span-3" />
              </div>
            )}
          </div>

          {canEdit && (
            <button onClick={saveHeader} disabled={savingHeader} data-testid="save-header-btn"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-60">
              {savingHeader ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save details
            </button>
          )}
        </div>
      )}

      {/* ---- LABOUR ---- */}
      {tab === 'labour' && (
        <LineItemGrid testId="grid-labour" title="Labour & Workforce" icon={Users} editable={canEdit}
          rows={diary.labor} onAdd={addRow('labor')} onUpdate={updateRow('labor')} onDelete={deleteRow('labor')}
          columns={[
            { key: 'company_name', label: 'Company', type: 'text', required: true, placeholder: 'Subcontractor / own' },
            { key: 'trade_type', label: 'Trade', type: 'select', options: enums.trade_types, default: 'general_labour' },
            { key: 'head_count', label: 'Head count', type: 'number' },
            { key: 'hours_per_worker', label: 'Hrs/worker', type: 'number' },
            { key: 'planned_hours', label: 'Planned hrs', type: 'number' },
            { key: 'total_hours_worked', label: 'Man-hours', type: 'number', readOnly: true, render: (r) => <span className="font-semibold text-teal-700">{r.total_hours_worked ?? 0}</span> },
            { key: 'activity_desc', label: 'Activity', type: 'textarea' },
          ]}
          // eslint-disable-next-line react/no-unstable-nested-components
          summary={(rows) => (
            <>
              <td className="px-3 py-2" colSpan={2}>Total</td>
              <td className="px-3 py-2">{rows.reduce((a, r) => a + (r.head_count || 0), 0)}</td>
              <td></td><td></td>
              <td className="px-3 py-2 text-teal-700">{rows.reduce((a, r) => a + (r.total_hours_worked || 0), 0)}h</td>
              <td></td>
            </>
          )}
        />
      )}

      {/* ---- EQUIPMENT ---- */}
      {tab === 'equipment' && (
        <LineItemGrid testId="grid-equipment" title="Plant & Equipment" icon={Truck} editable={canEdit}
          rows={diary.equipment} onAdd={addRow('equipment')} onUpdate={updateRow('equipment')} onDelete={deleteRow('equipment')}
          columns={[
            { key: 'equipment_type', label: 'Equipment', type: 'text', required: true },
            { key: 'equipment_model', label: 'Model', type: 'text' },
            { key: 'status', label: 'Status', type: 'select', options: enums.equipment_status, default: 'active' },
            { key: 'hours_used', label: 'Hrs used', type: 'number' },
            { key: 'hours_idle', label: 'Hrs idle', type: 'number' },
            { key: 'fuel_litres_added', label: 'Fuel (L)', type: 'number' },
            { key: 'breakdown_description', label: 'Breakdown / notes', type: 'textarea' },
          ]}
        />
      )}

      {/* ---- MATERIALS ---- */}
      {tab === 'materials' && (
        <LineItemGrid testId="grid-materials" title="Material Deliveries" icon={Package} editable={canEdit}
          rows={diary.deliveries} onAdd={addRow('deliveries')} onUpdate={updateRow('deliveries')} onDelete={deleteRow('deliveries')}
          columns={[
            { key: 'supplier_name', label: 'Supplier', type: 'text', required: true },
            { key: 'material_type', label: 'Material', type: 'text', required: true },
            { key: 'quantity', label: 'Qty', type: 'number' },
            { key: 'unit', label: 'Unit', type: 'select', options: enums.delivery_units, default: 'unit' },
            { key: 'status', label: 'Status', type: 'select', options: enums.delivery_status, default: 'approved' },
            { key: 'po_reference', label: 'PO ref', type: 'text' },
            { key: 'rejection_reason', label: 'Reject reason', type: 'text' },
          ]}
        />
      )}

      {/* ---- PROGRESS ---- */}
      {tab === 'progress' && (
        <LineItemGrid testId="grid-progress" title="Progress & Activities" icon={TrendingUp} editable={canEdit}
          rows={diary.progress} onAdd={addRow('progress')} onUpdate={updateRow('progress')} onDelete={deleteRow('progress')}
          columns={[
            { key: 'wbs_code', label: 'WBS', type: 'text' },
            { key: 'location_zone', label: 'Location', type: 'text' },
            { key: 'task_description', label: 'Task', type: 'textarea', required: true },
            { key: 'pct_complete_today', label: '% today', type: 'number' },
            { key: 'pct_complete_cumulative', label: '% cumul.', type: 'number' },
            { key: 'status', label: 'Status', type: 'select', options: enums.progress_status, default: 'in_progress' },
            { key: 'blocker_description', label: 'Blocker', type: 'text' },
          ]}
        />
      )}

      {/* ---- SAFETY ---- */}
      {tab === 'safety' && (
        <SafetyPanel diary={diary} canEdit={canEdit} onSaved={load} />
      )}

      {/* ---- VISITORS ---- */}
      {tab === 'visitors' && (
        <LineItemGrid testId="grid-visitors" title="Site Visitors" icon={UserCheck} editable={canEdit}
          rows={diary.visitors} onAdd={addRow('visitors')} onUpdate={updateRow('visitors')} onDelete={deleteRow('visitors')}
          columns={[
            { key: 'visitor_name', label: 'Name', type: 'text', required: true },
            { key: 'company', label: 'Company', type: 'text' },
            { key: 'purpose_of_visit', label: 'Purpose', type: 'select', options: enums.visitor_purposes, default: 'other' },
            { key: 'time_in', label: 'Time in', type: 'datetime' },
            { key: 'time_out', label: 'Time out', type: 'datetime' },
          ]}
        />
      )}

      {/* ---- MEDIA ---- */}
      {tab === 'media' && <MediaHub diaryId={id} canEdit={canEdit} />}

      {/* ---- SIGN-OFF ---- */}
      {tab === 'signoff' && <SignOffPanel diary={diary} isReviewer={isReviewer} onLocked={load} downloadPdf={downloadPdf} />}

      {/* ---- AUDIT ---- */}
      {tab === 'audit' && <AuditPanel diaryId={id} diary={diary} />}

      {/* Sticky workflow bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 z-30">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-end gap-2">
          {status === 'draft' && (
            <button onClick={doSubmit} data-testid="submit-btn" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium">
              <Send className="w-4 h-4" /> Submit for Review
            </button>
          )}
          {status === 'submitted' && isReviewer && (
            <>
              <button onClick={doReject} className="inline-flex items-center gap-2 text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-lg font-medium">
                <RotateCcw className="w-4 h-4" /> Reject
              </button>
              <button onClick={() => setTab('signoff')} data-testid="goto-signoff-btn" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium">
                <Lock className="w-4 h-4" /> Approve & Lock
              </button>
            </>
          )}
          {status === 'signed_off' && (
            <>
              <button onClick={downloadPdf} data-testid="pdf-btn" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg font-medium">
                <FileDown className="w-4 h-4" /> Download PDF
              </button>
              {isAdmin && (
                <button onClick={doReopen} data-testid="reopen-btn" className="inline-flex items-center gap-2 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 px-4 py-2 rounded-lg font-medium">
                  <RotateCcw className="w-4 h-4" /> Reopen / Amend
                </button>
              )}
            </>
          )}
          {isAdmin && status !== 'void' && (
            <button onClick={doVoid} className="inline-flex items-center gap-2 text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg text-sm">
              <Ban className="w-4 h-4" /> Void
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ Safety Panel ============ */
function SafetyPanel({ diary, canEdit, onSaved }) {
  const s = diary.safety || {};
  const [form, setForm] = useState({
    safety_walk_conducted: s.safety_walk_conducted || false,
    toolbox_talk_conducted: s.toolbox_talk_conducted || false,
    toolbox_talk_topic: s.toolbox_talk_topic || '',
    toolbox_talk_attendees: s.toolbox_talk_attendees ?? '',
    ppe_compliance_pct: s.ppe_compliance_pct ?? 100,
    ppe_notes: s.ppe_notes || '',
    has_incidents: s.has_incidents || false,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await siteDiaryAPI.saveSafety(diary.id, clean(form));
      await onSaved();
      toast.success('Safety saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save safety'); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      <h3 className="font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-600" /> Safety & HSEQ</h3>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" disabled={!canEdit} checked={form.safety_walk_conducted} onChange={(e) => setForm((f) => ({ ...f, safety_walk_conducted: e.target.checked }))} className="w-4 h-4 accent-teal-600" />
        Safety walk conducted
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" disabled={!canEdit} checked={form.toolbox_talk_conducted} onChange={(e) => setForm((f) => ({ ...f, toolbox_talk_conducted: e.target.checked }))} className="w-4 h-4 accent-teal-600" />
        Toolbox talk conducted
      </label>
      {form.toolbox_talk_conducted && (
        <div className="grid md:grid-cols-2 gap-3">
          <input disabled={!canEdit} placeholder="Toolbox talk topic" value={form.toolbox_talk_topic} onChange={(e) => setForm((f) => ({ ...f, toolbox_talk_topic: e.target.value }))} className="px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50" />
          <input disabled={!canEdit} type="number" placeholder="Attendees" value={form.toolbox_talk_attendees} onChange={(e) => setForm((f) => ({ ...f, toolbox_talk_attendees: e.target.value === '' ? '' : Number(e.target.value) }))} className="px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50" />
        </div>
      )}
      <div>
        <label className="text-sm font-medium text-slate-600">PPE compliance: {form.ppe_compliance_pct}%</label>
        <input disabled={!canEdit} type="range" min="0" max="100" value={form.ppe_compliance_pct} onChange={(e) => setForm((f) => ({ ...f, ppe_compliance_pct: Number(e.target.value) }))} className="w-full accent-teal-600" />
      </div>
      <textarea disabled={!canEdit} rows={2} placeholder="PPE / non-compliance notes" value={form.ppe_notes} onChange={(e) => setForm((f) => ({ ...f, ppe_notes: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg disabled:bg-slate-50" />
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" disabled={!canEdit} checked={form.has_incidents} onChange={(e) => setForm((f) => ({ ...f, has_incidents: e.target.checked }))} className="w-4 h-4 accent-red-600" />
        <AlertTriangle className="w-4 h-4 text-red-500" /> Incident(s) occurred today
      </label>
      {canEdit && (
        <button onClick={save} disabled={saving} data-testid="save-safety-btn" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save safety
        </button>
      )}
    </div>
  );
}

/* ============ Media Hub ============ */
function MediaHub({ diaryId, canEdit }) {
  const [items, setItems] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef(null);
  const [showCompare, setShowCompare] = useState(false);
  const [compare, setCompare] = useState(null);
  const [cmpThumbs, setCmpThumbs] = useState({});

  const loadCompare = useCallback(async () => {
    try {
      const res = await siteDiaryAPI.compare(diaryId);
      setCompare(res.data);
    } catch (e) { toast.error('Could not load comparison'); }
  }, [diaryId]);

  const toggleCompare = () => {
    const next = !showCompare;
    setShowCompare(next);
    if (next && !compare) loadCompare();
  };

  useEffect(() => {
    if (!compare) return;
    const all = [...(compare.current?.media || []), ...(compare.previous?.media || [])];
    all.forEach(async (m) => {
      if (cmpThumbs[m.id]) return;
      try { const b = await siteDiaryAPI.getMediaBlob(m.diary_id, m.id); setCmpThumbs((t) => ({ ...t, [m.id]: URL.createObjectURL(b.data) })); } catch { /* noop */ }
    });
  }, [compare, cmpThumbs]);

  const load = useCallback(async () => {
    try {
      const res = await siteDiaryAPI.listMedia(diaryId);
      setItems(res.data || []);
    } catch (e) {
      setItems([]);
    }
  }, [diaryId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    items.forEach(async (m) => {
      if (thumbs[m.id] || !(m.mime_type || '').startsWith('image/')) return;
      try { const b = await siteDiaryAPI.getMediaBlob(diaryId, m.id); setThumbs((t) => ({ ...t, [m.id]: URL.createObjectURL(b.data) })); } catch { /* noop */ }
    });
  }, [items, diaryId, thumbs]);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const res = await siteDiaryAPI.uploadMedia(diaryId, f, { category: 'general' });
        const warns = res.data.verification_warnings || [];
        if (warns.length) toast.warning(warns.join(' '));
        else toast.success(`${f.name} uploaded${res.data.geotag_lat ? ' (GPS verified)' : ''}`);
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const del = async (mid) => {
    try { await siteDiaryAPI.deleteMedia(diaryId, mid); await load(); toast.success('Removed'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Camera className="w-4 h-4 text-teal-600" /> Site Photos &amp; Documents</h3>
        <button onClick={toggleCompare} data-testid="compare-toggle"
          className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border font-medium ${showCompare ? 'bg-teal-600 text-white border-teal-600' : 'text-teal-700 border-teal-300 hover:bg-teal-50'}`}>
          <Columns className="w-4 h-4" /> Compare days
        </button>
      </div>

      {showCompare && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="compare-panel">
          {!compare ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
          ) : !compare.previous ? (
            <p className="text-sm text-slate-400 text-center py-6">No earlier diary found for this project to compare against.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {[['previous', 'Previous', compare.previous], ['current', 'Today', compare.current]].map(([k, lbl, col]) => (
                <div key={k}>
                  <div className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1">
                    {lbl} <span className="text-slate-400 normal-case font-normal">· {col.diary_date}</span>
                  </div>
                  {col.media.length === 0 ? (
                    <p className="text-xs text-slate-400">No photos</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {col.media.map((m) => (
                        <div key={m.id} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                          {cmpThumbs[m.id] ? <img src={cmpThumbs[m.id]} alt={m.caption || m.file_name} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(Array.from(e.dataTransfer.files)); }}
          onClick={() => fileInput.current?.click()}
          data-testid="media-dropzone"
          className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${dragOver ? 'border-teal-500 bg-teal-50' : 'border-slate-300 bg-white hover:border-teal-300'}`}>
          {uploading ? <Loader2 className="w-8 h-8 mx-auto animate-spin text-teal-600" /> : <Upload className="w-8 h-8 mx-auto text-slate-400" />}
          <p className="mt-2 text-sm text-slate-600 font-medium">Drag & drop photos, or click to browse</p>
          <p className="text-xs text-slate-400">GPS &amp; timestamp are auto-extracted to verify on-site capture.</p>
          <input ref={fileInput} type="file" multiple accept="image/*,application/pdf" className="hidden"
            onChange={(e) => upload(Array.from(e.target.files))} data-testid="media-file-input" />
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-center py-10 text-slate-400"><Camera className="w-10 h-10 mx-auto mb-2 opacity-40" />No media uploaded.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((m) => (
            <div key={m.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden group relative">
              <div className="aspect-square bg-slate-100 flex items-center justify-center">
                {thumbs[m.id] ? <img src={thumbs[m.id]} alt={m.file_name} className="w-full h-full object-cover" />
                  : <Package className="w-8 h-8 text-slate-300" />}
              </div>
              <div className="p-2">
                <p className="text-xs text-slate-600 truncate">{m.caption || m.file_name}</p>
                {(m.geotag_lat != null) && <p className="text-[10px] text-green-600 flex items-center gap-0.5"><MapPin className="w-3 h-3" />GPS tagged</p>}
                {(m.verification_warnings || []).length > 0 && <p className="text-[10px] text-amber-600 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />{m.verification_warnings.length} warning</p>}
              </div>
              {canEdit && <button onClick={() => del(m.id)} className="absolute top-1 right-1 p-1 bg-white/90 rounded text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ Sign-off Panel ============ */
function SignOffPanel({ diary, isReviewer, onLocked, downloadPdf }) {
  const [sig, setSig] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientSig, setClientSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const status = diary.status;

  const approve = async () => {
    if (!sig) { toast.error('Please capture the Site Manager signature'); return; }
    setBusy(true);
    try {
      await siteDiaryAPI.lock(diary.id, { signature: sig, client_rep_name: clientName || null, client_rep_signature: clientSig || null });
      await onLocked();
      toast.success('Diary approved & locked');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not approve'); }
    finally { setBusy(false); }
  };

  const genShare = async () => {
    setSharing(true);
    try {
      const res = await siteDiaryAPI.share(diary.id);
      const url = `${window.location.origin}${res.data.path}`;
      setShareUrl(url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create link'); }
    finally { setSharing(false); }
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); }
    catch { toast.message(shareUrl); }
  };

  const shareBox = (
    <div className="mt-5 border-t border-slate-100 pt-4 text-left">
      <p className="text-sm font-medium text-slate-700 flex items-center gap-2 justify-center"><Share2 className="w-4 h-4 text-teal-600" /> Client portal</p>
      <p className="text-xs text-slate-400 text-center mb-3">Share a read-only link so the client rep can review and sign from their phone.</p>
      {!shareUrl ? (
        <button onClick={genShare} disabled={sharing} data-testid="share-btn"
          className="mx-auto flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-60">
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Generate share link
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <input readOnly value={shareUrl} data-testid="share-url"
            className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50" />
          <button onClick={copyShare} className="p-2 border border-slate-300 rounded-lg hover:bg-slate-100"><Copy className="w-4 h-4 text-slate-600" /></button>
        </div>
      )}
    </div>
  );

  if (status === 'signed_off') {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="font-semibold text-slate-800">Approved & Locked</h3>
        <p className="text-sm text-slate-500 mt-1">Signed off by {diary.signed_off_by_name} on {diary.signed_off_at?.slice(0, 16).replace('T', ' ')}</p>
        {diary.client_rep_name
          ? <p className="text-sm text-green-600 mt-1 flex items-center gap-1 justify-center"><CheckCircle2 className="w-4 h-4" /> Counter-signed by client: {diary.client_rep_name}</p>
          : <p className="text-sm text-slate-400 mt-1">Awaiting client counter-signature</p>}
        <button onClick={downloadPdf} className="mt-4 inline-flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg text-sm"><FileDown className="w-4 h-4" /> Download PDF</button>
        {isReviewer && shareBox}
      </div>
    );
  }

  if (status !== 'submitted') {
    return <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center text-slate-500">
      The diary must be <span className="font-medium">submitted for review</span> before it can be approved & locked.
    </div>;
  }
  if (!isReviewer) {
    return <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center text-slate-500">
      Only a site/project manager can approve & lock this diary.
    </div>;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5">
      <h3 className="font-semibold text-slate-800 flex items-center gap-2"><PenTool className="w-4 h-4 text-teal-600" /> Digital Sign-off</h3>
      <SignaturePad label="Site Manager signature *" onChange={setSig} />
      <div className="border-t border-slate-100 pt-4">
        <input placeholder="Client representative name (optional)" value={clientName} onChange={(e) => setClientName(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg mb-3" data-testid="client-rep-name" />
        <SignaturePad label="Client Representative signature (optional)" onChange={setClientSig} />
      </div>
      <button onClick={approve} disabled={busy} data-testid="approve-lock-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Approve & Lock Diary
      </button>
      <p className="text-xs text-slate-400 text-center">Locking generates the PDF summary and pushes labour, delivery and progress data to the connected modules.</p>
    </div>
  );
}

/* ============ Audit Panel ============ */
function AuditPanel({ diaryId, diary }) {
  const [data, setData] = useState(null);
  const [diff, setDiff] = useState(null);
  useEffect(() => {
    siteDiaryAPI.auditLog(diaryId).then((r) => setData(r.data)).catch(() => setData({ audit_log: [], signoff_chain: [] }));
    if (diary.parent_diary_id) siteDiaryAPI.amendmentDiff(diaryId).then((r) => setDiff(r.data)).catch(() => {});
  }, [diaryId, diary.parent_diary_id]);

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  return (
    <div className="space-y-4">
      {diff && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <h3 className="font-semibold text-indigo-800 flex items-center gap-2 mb-2"><History className="w-4 h-4" /> Amendment v{diff.version} (from v{diff.parent_version})</h3>
          {Object.keys(diff.header_changes).length === 0 ? <p className="text-sm text-indigo-600">No header field changes yet.</p> : (
            <ul className="text-sm text-indigo-700 space-y-1">
              {Object.entries(diff.header_changes).map(([k, v]) => (
                <li key={k} className="flex items-center gap-2"><span className="font-medium capitalize">{k.replace(/_/g, ' ')}:</span>
                  <span className="line-through text-indigo-400">{String(v.old ?? '—')}</span><ChevronRight className="w-3 h-3" /><span>{String(v.new ?? '—')}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Sign-off chain</h3>
        {data.signoff_chain.length === 0 ? <p className="text-sm text-slate-400">No workflow events yet.</p> : (
          <ol className="relative border-l border-slate-200 ml-2 space-y-3">
            {data.signoff_chain.map((s) => (
              <li key={s.id} className="ml-4">
                <div className="absolute w-2 h-2 bg-teal-500 rounded-full -left-1 mt-1.5" />
                <p className="text-sm text-slate-700"><span className="font-medium capitalize">{s.action.replace(/_/g, ' ')}</span> by {s.actor_name} <span className="text-slate-400">({s.actor_role})</span></p>
                <p className="text-xs text-slate-400">{s.event_at?.slice(0, 16).replace('T', ' ')} · {s.previous_status} → {s.new_status}{s.comments ? ` · "${s.comments}"` : ''}</p>
              </li>
            ))}
          </ol>
        )}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Change log ({data.audit_log.length})</h3>
        <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
          {data.audit_log.map((a) => (
            <div key={a.id} className="py-2 text-sm">
              <span className="capitalize font-medium text-slate-700">{a.action}</span>
              <span className="text-slate-500"> · {a.entity_type}</span>
              <span className="text-slate-400"> · {a.actor_name} · {a.event_at?.slice(0, 16).replace('T', ' ')}</span>
              {a.field_changes && Object.keys(a.field_changes).length > 0 && (
                <span className="text-xs text-slate-400"> ({Object.keys(a.field_changes).join(', ')})</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
